import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import PromoCodeInput from "@/components/PromoCodeInput";
import CompBanner from "@/components/CompBanner";
import { CoinsIcon as Coins } from "@phosphor-icons/react";
import { isNativeApp, isIOS, webOrigin } from "@/lib/nativeBridge";
import IosPurchaseNotice from "@/components/IosPurchaseNotice";

/**
 * Confirm-pay dialog for a single video tour booking.
 *
 * Promotes the previously-inline "Pay $X to confirm" button to a
 * gated dialog so buyers can apply a promo code before being kicked
 * out to Stripe. Mirrors the pattern already in
 * ListDogDialog/RenewDialog/TransferPay so promo UX is consistent
 * across every paywalled CTA.
 *
 * On success Stripe checkout opens in the same tab.
 */
export default function VideoTourPayDialog({ open, onClose, tour, tourPriceUsd }) {
  const [promo, setPromo] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!tour?.id) return;
    setBusy(true);
    try {
      const r = await api.post("/billing/checkout", {
        package_id: "video_tour",
        origin_url: webOrigin(),
        tour_id: tour.id,
        promo_code: promo?.valid ? promo.code : undefined,
      });
      if (r.data?.url) {
        window.location.href = r.data.url;
        return;
      }
      // Free path (100%-off promo or admin freebie) — short-circuit
      toast.success("Tour confirmed.");
      onClose?.(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (isIOS()) {
    return (
      <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
        <DialogContent className="max-w-sm" data-testid="video-tour-pay-dialog">
          <DialogHeader>
            <DialogTitle className="font-spectral text-xl flex items-center gap-2">
              <Coins size={20} weight="duotone" /> Confirm tour booking
            </DialogTitle>
            <DialogDescription>Tour payments aren&rsquo;t available in the app.</DialogDescription>
          </DialogHeader>
          <IosPurchaseNotice label="Tour booking payment" showPlans={false} />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-sm" data-testid="video-tour-pay-dialog">
        <DialogHeader>
          <DialogTitle className="font-spectral text-xl flex items-center gap-2">
            <Coins size={20} weight="duotone" /> Confirm tour booking
          </DialogTitle>
          <DialogDescription>
            Pay <strong>${tourPriceUsd.toFixed(2)}</strong> to lock in the tour with{" "}
            {tour?.seller_name || "the seller"}. Tax + Stripe processing applied
            at checkout.
          </DialogDescription>
        </DialogHeader>
        <CompBanner className="mb-3" />
        <PromoCodeInput
          kind="video_tour"
          amount={tourPriceUsd}
          applied={promo}
          onApplied={(p) => {
            setPromo(p);
            toast.success(`${p.code} applied — final $${p.final.toFixed(2)}`);
          }}
          onCleared={() => setPromo(null)}
        />
        <Button
          onClick={submit}
          disabled={busy}
          className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white mt-1"
          data-testid="video-tour-pay-submit-btn"
        >
          {busy
            ? "Redirecting…"
            : isNativeApp()
            ? `Confirm — $${(promo?.valid ? promo.final : tourPriceUsd).toFixed(2)} checkout in browser`
            : promo?.valid
            ? `Pay $${promo.final.toFixed(2)} & confirm`
            : `Pay $${tourPriceUsd.toFixed(2)} & confirm`}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
